import type { Lesson } from "@edu/shared";

/**
 * Сколько человек стоит за строками списка «Уроки по этой заготовке».
 *
 * ⚠️ **Считаются люди, а не строки, и до 02.09.2026 это было одно и то же.**
 * Свёртка занятия группы (2.3 `docs/group-room-design.md`) заменила двенадцать
 * строк одной, а у свёрнутой строки ученика нет вовсе: `studentUserId` там
 * `null` намеренно, потому что подставить одного из двенадцати нельзя. Счёт по
 * этому полю показывал бы «1 занятие, 0 учеников» ровно там, где занималась
 * целая группа.
 *
 * Правило простое: у свёрнутой строки людей столько, сколько говорит размер
 * комнаты, у обычной один, а у урока без названного ученика ни одного.
 *
 * ⚠️ **Занятия одной группы не складываются.** Заготовку проводят по группе
 * несколько раз, и три занятия по двенадцать человек это те же двенадцать, а не
 * тридцать шесть.
 *
 * ⚠️ **Одна группа узнаётся по ИДЕНТИФИКАТОРУ, и сшивать по названию нельзя.**
 * Название выбирает человек, оно не уникально, и «9Б» у одного преподавателя
 * бывает и по понедельникам, и по четвергам, и за прошлый год: сшивка по нему
 * склеила бы две разных группы в одну и занизила счёт. Это тот же довод, по
 * которому список участников сшивает присутствие с составом по `uid`, а не по
 * подписи (`participant-rows.ts`), и ради него идентификатор и заведён в
 * `LessonGroup`.
 *
 * Берётся наибольший размер: состав группы за четверть меняется, и «сколько
 * человек прошло по этой заготовке» это самое многолюдное занятие, а не
 * последнее.
 *
 * ⚠️ Ученик, занимавшийся и лично, и в группе, посчитается дважды: узнать его в
 * составе группы нам нечем, имён оттуда в строке нет. Цена принята, потому что
 * альтернатива это тянуть состав каждой группы ради подписи в шапке.
 *
 * Чистая функция со своими проверками, потому что правил здесь три и каждое
 * ломается молча: экран покажет неверное число, а не упадёт.
 */
export function lessonPeopleCount(lessons: readonly Lesson[]): number {
  /** Названные ученики: один и тот же человек в пяти уроках это один человек. */
  const alone = new Set<string>();
  /** Самое многолюдное занятие каждой группы, по её идентификатору. */
  const groups = new Map<string, number>();
  /** Строки группы, которые слить не с чем. Обе причины разобраны в ветках. */
  let unmerged = 0;

  for (const lesson of lessons) {
    if (lesson.kind === "group") {
      const group = lesson.group;
      /*
        Занятие группы без названия: сегодня недостижимо (сеанс держит группу
        внешним ключом), и тогда у нас нет ни размера, ни личности группы.
        Строка идёт за одного человека: занятие с людьми точно было, а ноль
        сказал бы, что не было никого.
      */
      if (group === undefined) {
        unmerged += 1;
        continue;
      }
      /*
        Размер есть, а идентификатора нет: так отвечает API старше 02.09.2026,
        и это окно выкатки, а не рабочее состояние. Считаем строку отдельно и по
        названию НЕ сливаем: угадывание по подписи запрещено выше, а завышенный
        на минуту счётчик в подписи шапки дешевле склеенных разных групп.
      */
      if (group.id === undefined) {
        unmerged += group.size;
        continue;
      }
      groups.set(group.id, Math.max(groups.get(group.id) ?? 0, group.size));
      continue;
    }
    if (lesson.studentUserId != null) alone.add(lesson.studentUserId);
  }

  let total = alone.size + unmerged;
  for (const size of groups.values()) total += size;
  return total;
}
